import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Redirect } from 'react-router';
import { gql, useQuery } from '@apollo/client';

import { ASSESSMENT_STAGE_FIELDS } from '../../../lib/queries/assessment-stage.queries';
import { getAuthUser } from '../../../lib/services/auth.service';
import { AssessmentStageBuilderContainer } from './assessment-stage-builder.container';
import { buildUpdateAgeGroup, getAgeGroups } from '../../../lib/services/age-group.service';
import {
  buildCompleteAssessmentStage,
  buildUpdateAssessmentStage,
} from '../../../lib/services/assessment-stage.service';
import { useCompanyHasAutomation, useCompanySport } from '../../../lib/stores/company.store';
import { UseTemplateModalContainer } from '../../FormatTemplate/UseTemplateModal/use-template-modal.container';
import { useStageReferenceData } from '../use-stage-reference-data';
import { ROLES } from '../../../../_configuration';
import { useStageStructureActions } from './use-stage-structure-actions';
import { useOperationErrorHandler } from '../../../lib/hooks/use-operation-error-handler';
import { messages } from './assessment-stage-builder.messages';

const GET_ASSESSMENT_STAGE_BUILDER = gql`
  query GetAssessmentStageBuilder($assessmentId: ID!, $ageGroupId: ID!) {
    assessmentStages(assessmentId: $assessmentId, ageGroupId: $ageGroupId) {
      ...AssessmentStageFields
    }
  }

  ${ASSESSMENT_STAGE_FIELDS}
`;

export const AssessmentStageBuilderGraphQL = () => {
  const hasAutomation = useCompanyHasAutomation();
  const sport = useCompanySport();

  const user = getAuthUser();
  const { ageGroups } = getAgeGroups({ requiredRoles: [ROLES.CONFIGURATION] });
  const structure = useStageStructureActions();
  const { completeAssessmentStage } = buildCompleteAssessmentStage();
  const { updateAssessmentStage } = buildUpdateAssessmentStage();
  const { updateAgeGroup } = buildUpdateAgeGroup();

  const reportCompleteFailure = useOperationErrorHandler(messages.stageNotCompleted);

  const [pickedAgeGroupId, setAgeGroupId] = useState<string>();
  const [useTemplateOpen, setUseTemplateOpen] = useState(false);
  const [isCompletingStage, setCompletingStage] = useState(false);
  const completeInFlight = useRef(false);

  // Not memoized: a scalar pick; a lone age group is auto-selected until the user picks one.
  const ageGroupId = pickedAgeGroupId ?? (ageGroups.length === 1 ? ageGroups[0]._id : undefined);

  const ageGroup = useMemo<IAgeGroup | null>(
    () => ageGroups.find(ag => ag._id === ageGroupId) ?? null,
    [ageGroups, ageGroupId],
  );

  const { data, loading, error } = useQuery(GET_ASSESSMENT_STAGE_BUILDER, {
    skip: !user || !ageGroup,
    variables: {
      assessmentId: user?.settings.activeAssessmentId,
      ageGroupId: ageGroup?._id,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { drillOptions, practicePlanOptions, positionOptions, loading: referenceLoading } = useStageReferenceData({
    companyId: user?.settings.activeCompanyId,
    sport,
    includeGlobal: user?.settings.includeGlobal,
    skip: !user || !ageGroup,
  });

  // Memoized so the `|| []` fallback doesn't hand a fresh array to consumers each render —
  // stabilizes the completeStage useCallback below.
  const stages = useMemo(() => (data && data.assessmentStages) || [], [data]);

  const handleManuallyCompleteAssessmentStage = useCallback(async ({ _id }: IAssessmentStage) => {
    if (!ageGroup || completeInFlight.current) return;
    if (!window.confirm('Are you sure you want to mark this stage as complete?')) return;
    completeInFlight.current = true;
    setCompletingStage(true);
    try {
      try {
        await completeAssessmentStage(_id);
      } catch (error) {
        reportCompleteFailure('completeAssessmentStage', error);
        return;
      }
      try {
        await updateAgeGroup(ageGroup, stages);
      } catch (error) {
        reportCompleteFailure('updateAgeGroup (advance stage)', error, { message: messages.stageNotAdvanced });
      }
    } finally {
      completeInFlight.current = false;
      setCompletingStage(false);
    }
  }, [ageGroup, completeAssessmentStage, reportCompleteFailure, stages, updateAgeGroup]);

  if (!hasAutomation) return <Redirect to="/" />;

  return (
    <>
      <AssessmentStageBuilderContainer
        loading={loading || referenceLoading}
        fetchError={Boolean(error)}
        setAgeGroupId={setAgeGroupId}
        stages={stages}
        drillOptions={drillOptions}
        practicePlanOptions={practicePlanOptions}
        ageGroups={ageGroups}
        ageGroup={ageGroup}
        positionOptions={positionOptions}
        companyId={user?.settings.activeCompanyId || ''}
        completeStage={handleManuallyCompleteAssessmentStage}
        {...structure}
        isChangingStructure={structure.isChangingStructure || isCompletingStage}
        updateStage={updateAssessmentStage}
        onOpenUseTemplate={() => setUseTemplateOpen(true)}
      />
      {user?.settings.activeAssessmentId && user?.settings.activeCompanyId && (
        <UseTemplateModalContainer
          open={useTemplateOpen}
          onClose={() => setUseTemplateOpen(false)}
          assessmentId={user.settings.activeAssessmentId}
          ageGroup={ageGroup}
          companyId={user.settings.activeCompanyId}
        />
      )}
    </>
  );
};
